<template>
  <div>
    <div class="sf-grid sf-bg-snow sf-pad-2">
      <div class="sf-row">
        <div class="sf-col">
          <div class="sf-tab sf-level">
            <span
              class="sf-pad-l-1 sf-pad-r-1 sf-level-item sf-c-text-light"
              style="margin-right: 0 !important"
              ><i class="i-search"></i
            ></span>
            <!-- TODO: Use search component comment here once it's ready -->
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="
                I18n.t(
                  'components.document.existing_docs_modal.search_placeholder'
                )
              "
              class="
                sf-form-input sf-full sf-no-border sf-pad-l-0 sf-level-item
              "
              @keyup="search"
            />
          </div>
        </div>
      </div>

      <div class="sf-row sf-below-0">
        <div class="sf-col">
          <document-breadcrumbs
            :parent-category="parentCategory"
            :permanent-category="permanentCategory"
            :navigate-function="navigate"
            :navigate-params="{ id: parentCategory ? parentCategory.id : null }"
          />
        </div>
      </div>

      <div class="sf-row">
        <div class="sf-col" style="max-height: 378px; overflow-y: auto">
          <div
            v-if="entries.length === 0 && !searchQuery"
            class="
              sf-alignc sf-c-text sf-bg-snow-dark sf-font-l sf-pad-4 sf-dim-el
            "
          >
            {{ I18n.t('permanent_document.modal_select.empty_folder') }}
          </div>
          <table v-else class="sf-table sf-table-border sf-table-middle">
            <thead>
              <tr>
                <th>
                  <div class="sf-level sf-level-between">
                    <div class="sf-level-item">
                      {{
                        I18n.t(
                          'components.document.existing_docs_modal.document_header'
                        )
                      }}
                    </div>
                    <div class="sf-level-item">
                      {{
                        I18n.t(
                          'components.document.existing_docs_modal.attachments'
                        )
                      }}
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in entries" :key="entry.id">
                <td
                  :class="tdClasses(entry)"
                  :data-testid="`document-entry-${entry.title}-${
                    selectedEntriesIds.includes(entry.id)
                      ? 'selected'
                      : 'unselected'
                  }`"
                  @click="entryClick(entry)"
                >
                  <div class="sf-level sf-level-between">
                    <div class="sf-level-item">
                      <div class="sf-level sf-level-small">
                        <span :class="entry.icon">
                          <span
                            v-if="entry.invisible"
                            class="
                              icon-eye-slash
                              documents-document-icon
                              documents-lock-icon
                              documents-eye-slash-icon-overlay
                              document-selector-eye-slash-icon-overlay
                            "
                          />
                        </span>
                        <div class="sf-break-all">
                          {{ entry.title }}
                        </div>
                        <span v-if="entry.count" class="sf-dim-el">
                          ({{ itemsTotal(entry) }})
                        </span>
                      </div>
                    </div>
                    <div v-if="entry.attachments" class="sf-level-item">
                      <span class="sf-font-xs sf-c-text-light sf-pad-r-1">
                        {{ entry.attachments.length }}
                      </span>
                      <span
                        class="sf-c-text-light icon icon-info-circle"
                        data-toggle="tooltip"
                        data-placement="bottom"
                        :title="attachmentsList(entry)"
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <SfPagination
        v-if="totalPages && totalPages > 1"
        :current-page="currentPage"
        :total-pages="totalPages"
        @change-page="changePage"
      />
    </div>

    <div class="sf-pad-t-2">
      <div class="sf-level sf-level-center">
        <div>
          <a href="#" class="sf-btn" @click.prevent="backClick">
            {{ I18n.t('components.document.existing_docs_modal.back') }}
          </a>
        </div>
        <div>
          <a
            href="#"
            class="sf-btn sf-btn-primary"
            :class="{ 'sf-disabled': selectedEntriesIds.length === 0 }"
            @click.prevent="attachClick"
          >
            {{ successBtnTitle }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import documentBreadcrumbs from '@app/application/document/sections/document-breadcrumbs'

import useI18n from '@app/shared/vue/composables/use-I18n'
import SfPagination from '@app/shared/vue/components/sf-pagination/sf-pagination.vue'

export default {
  name: 'ExistingDocumentsTable',

  components: {
    SfPagination,
    documentBreadcrumbs,
  },

  props: {
    entries: {
      type: Array,
      required: true,
      default: () => [],
    },
    selectedEntriesIds: {
      type: Array,
      required: true,
      default: () => [],
    },
    toggleSelection: {
      type: Function,
      required: true,
    },
    navigate: {
      type: Function,
      required: true,
    },
    permanentCategory: {
      type: Object,
      default: () => {},
    },
    replacement: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
  },

  emits: ['attach', 'back', 'search', 'change-page'],

  setup() {
    const I18n = useI18n()

    return {
      I18n,
    }
  },

  data() {
    return {
      searchQuery: '',
    }
  },

  computed: {
    parentCategory() {
      return this.permanentCategory && this.permanentCategory.parentCategory
    },
    successBtnTitle() {
      return this.replacement
        ? I18n.t('components.document.existing_docs_page.replace')
        : I18n.t('components.document.existing_docs_modal.attach')
    },
  },

  methods: {
    tdClasses(entry) {
      return `${this.entryHighlightingColor(entry)} sf-pointer`
    },

    itemsTotal(entry) {
      let text = `${entry.count} `

      if (entry.count === 1) {
        text += I18n.t('components.document.existing_docs_modal.item')
      } else {
        text += I18n.t('components.document.existing_docs_modal.items')
      }

      return text
    },

    entryHighlightingColor(entry) {
      return this.selectedEntriesIds.includes(entry.id)
        ? 'sf-bg-primary-subtle'
        : ''
    },

    entryClick(entry) {
      if (entry.category) {
        this.navigate(entry)
      } else {
        this.toggleSelection(entry)
      }
    },

    attachmentsList(entry) {
      const attachments = entry.attachments.slice(0, 6)

      if (entry.attachments.length > 6) attachments.push('and more ...')

      return attachments.join('\n')
    },

    changePage(page) {
      this.$emit('change-page', page)
    },

    search() {
      this.$emit('search', this.searchQuery)
    },

    backClick() {
      this.$emit('back')
    },

    attachClick() {
      this.$emit('attach')
    },

    documentsIconClass(entry) {
      const commonClasses = 'documents-document-icon sf-font-m sf-c-light'

      if (entry.type === 'document') {
        return `i-documents-${this.documentsIconClassForExtension(
          entry.extension
        )} ${commonClasses}`
      } else {
        return `i-documents-folder ${commonClasses}`
      }
    },
    documentsIconClassForExtension(extension) {
      switch (extension) {
        case ('.doc', '.docx', '.rtf', '.dot'):
          return 'file-word'
        case ('.xls', '.xlt', '.xlm', '.xlsx', '.xlsm'):
          return 'file-excel'
        case ('.ppt', '.pptx', '.pptm'):
          return 'file-powerpoint'
        case '.pdf':
          return 'file-pdf'
        default:
          return 'file'
      }
    },
  },
}
</script>
